import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

// helpers
import {
  getAffiliates as getAffiliateList,
  createAffiliateApi,
  getAffiliate as getAffiliateApi,
  updateAffiliateApi,
  deleteAffiliateApi,
} from "../../helpers/";

// constants
import { AffiliatesActionTypes } from "./constants";
import {
  createAffiliateError,
  createAffiliateSuccess,
  affiliatesGetListError,
  affiliatesGetListSuccess,
  getAffiliateSuccess,
  getAffiliateError,
  updateAffiliateSuccess,
  updateAffiliateError,
  deleteAffiliateSuccess,
  deleteAffiliateError,
} from "./actions";

interface AffiliateData {
  payload: {
    limit: number;
    skip: number;
    keyword?: string;
    owner?: string;
    country?: string;
    startDate?: string;
    endDate?: string;
  };
  type: string;
}

interface AffiliateCreateData {
  payload: {
    country: string;
    comment: string;
    description: string;
    name: string;
    price: number;
    productImage: string;
    quantity: number;
    availableCountries?: string[];
    prices?: { country: string; price: number }[];
  };
  type: string;
}

interface AffiliateUpdateData {
  payload: {
    id: string;
    comment: string;
    description: string;
    name: string;
    price: number;
    productImage: string;
    quantity: number;
  };
  type: string;
}

interface AffiliateDeleteData {
  payload: {
    ids: string[];
  };
  type: string;
}

interface GetAffiliateData {
  payload: {
    id: string;
  };
  type: string;
}

function* getAffiliates({
  payload: { limit, skip, keyword, owner, country, startDate, endDate },
}: AffiliateData): SagaIterator {
  try {
    const params: any = { limit, skip, keyword, owner, country };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = yield call(getAffiliateList, params);
    const affiliates = response.data;
    yield put(
      affiliatesGetListSuccess(
        AffiliatesActionTypes.GET_LIST_AFFILIATES,
        affiliates
      )
    );
  } catch (error: any) {
    yield put(
      affiliatesGetListError(AffiliatesActionTypes.GET_LIST_AFFILIATES, error)
    );
  }
}

function* createAffiliate({
  payload: {
    country,
    comment,
    description,
    name,
    price,
    productImage,
    quantity,
    availableCountries,
    prices,
  },
}: AffiliateCreateData): SagaIterator {
  try {
    const response = yield call(createAffiliateApi, {
      country,
      comment,
      description,
      name,
      price,
      productImage,
      quantity,
      availableCountries,
      prices,
    });
    const Affiliate = response.data;
    yield put(
      createAffiliateSuccess(AffiliatesActionTypes.CREATE_AFFILIATE, Affiliate)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response.data;
    yield put(
      createAffiliateError(AffiliatesActionTypes.CREATE_AFFILIATE, errorMessage)
    );
  }
}

function* updateAffiliate({
  payload: { id, comment, description, name, price, productImage, quantity },
}: AffiliateUpdateData): SagaIterator {
  try {
    const response = yield call(updateAffiliateApi, {
      id,
      comment,
      description,
      name,
      price,
      productImage,
      quantity,
    });
    const Affiliate = response.data;
    yield put(
      updateAffiliateSuccess(AffiliatesActionTypes.UPDATE_AFFILIATE, Affiliate)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response.data;
    yield put(
      updateAffiliateError(AffiliatesActionTypes.UPDATE_AFFILIATE, errorMessage)
    );
  }
}

function* deleteAffiliate({
  payload: { ids },
}: AffiliateDeleteData): SagaIterator {
  try {
    const response = yield call(deleteAffiliateApi, {
      ids,
    });
    const Affiliate = response.data;
    yield put(
      deleteAffiliateSuccess(AffiliatesActionTypes.DELETE_AFFILIATE, Affiliate)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response.data;
    yield put(
      deleteAffiliateError(AffiliatesActionTypes.DELETE_AFFILIATE, errorMessage)
    );
  }
}

function* getAffiliate({ payload: { id } }: GetAffiliateData): SagaIterator {
  try {
    const response = yield call(getAffiliateApi, { id });
    const Affiliate = response.data;
    yield put(
      getAffiliateSuccess(AffiliatesActionTypes.GET_AFFILIATE, Affiliate)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response.data;
    yield put(
      getAffiliateError(AffiliatesActionTypes.GET_AFFILIATE, errorMessage)
    );
  }
}

export function* watchGetAffiliates() {
  yield takeEvery(AffiliatesActionTypes.GET_LIST_AFFILIATES, getAffiliates);
}

export function* watchCreateAffiliates() {
  yield takeEvery(AffiliatesActionTypes.CREATE_AFFILIATE, createAffiliate);
}

export function* watchUpdateAffiliates() {
  yield takeEvery(AffiliatesActionTypes.UPDATE_AFFILIATE, updateAffiliate);
}

export function* watchDeleteAffiliates() {
  yield takeEvery(AffiliatesActionTypes.DELETE_AFFILIATE, deleteAffiliate);
}

export function* watchGetAffiliate() {
  yield takeEvery(AffiliatesActionTypes.GET_AFFILIATE, getAffiliate);
}

function* affiliatesSaga() {
  yield all([
    fork(watchGetAffiliates),
    fork(watchCreateAffiliates),
    fork(watchGetAffiliate),
    fork(watchUpdateAffiliates),
    fork(watchDeleteAffiliates),
  ]);
}

export default affiliatesSaga;
